import { useCallback, useRef, useState } from 'react';
import { uploadFile } from '../lib/uploader.js';
import { formatBytes } from '../lib/api.js';

/**
 * Upload queue UI.
 *
 * FILE_CONCURRENCY = 2: two files uploading at once. Photos finish fast enough
 * that more parallelism mostly adds memory pressure; the multi-GB video gets
 * its own internal part-level parallelism (4) from the uploader.
 *
 * The queue survives a page refresh only in the sense that R2 keeps the
 * partially-uploaded parts and localStorage keeps the assetId - drop the same
 * files in again and each one resumes from where it stopped rather than
 * restarting. That is the pragmatic version of resumability: no service worker,
 * no background sync, no server-held state.
 */

const FILE_CONCURRENCY = 2;

export default function Uploader({ galleryId, onComplete }) {
  const [items, setItems] = useState([]); // {id, file, progress, status, error}
  const [warnings, setWarnings] = useState([]);
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const update = (id, patch) => setItems((list) => list.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const start = useCallback(
    async (files) => {
      const fresh = files.map((file, i) => ({
        id: `${Date.now()}-${i}-${file.name}`,
        file,
        progress: 0,
        status: 'queued',
      }));
      setItems((list) => [...list, ...fresh]);

      const controller = new AbortController();
      abortRef.current = controller;

      let cursor = 0;
      const worker = async () => {
        while (cursor < fresh.length) {
          const item = fresh[cursor++];
          try {
            await uploadFile({
              file: item.file,
              galleryId,
              signal: controller.signal,
              onProgress: (p) => update(item.id, { progress: p }),
              onStatus: (s) => update(item.id, { status: s }),
              onWarning: (w) => setWarnings((list) => [...list, { ...w, id: `${item.id}-${w.title}` }]),
            });
            update(item.id, { status: 'done', progress: 1 });
          } catch (err) {
            update(item.id, { status: err.cancelled ? 'cancelled' : 'failed', error: err.message });
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(FILE_CONCURRENCY, fresh.length) }, worker));
      onComplete?.();
    },
    [galleryId, onComplete],
  );

  const pick = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (files.length) start(files);
  };

  const active = items.filter((i) => !['done', 'cancelled'].includes(i.status));
  const failed = items.filter((i) => i.status === 'failed');

  return (
    <div className="stack">
      <div
        className={`dropzone ${over ? 'over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          pick(e.dataTransfer.files);
        }}
      >
        <p style={{ marginBottom: '0.5rem' }}>Drop photos and video here</p>
        <p className="tiny" style={{ marginBottom: '0.75rem' }}>
          Large files upload in chunks straight to R2 and resume if interrupted — drop the same file again to continue it.
        </p>
        <button type="button" onClick={() => inputRef.current?.click()}>
          Choose files
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            pick(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {warnings.map((w) => (
        <div key={w.id} className={w.level === 'warning' ? 'error' : 'notice'}>
          <div className="spread">
            <strong>{w.title}</strong>
            <button
              className="ghost sm"
              onClick={() => setWarnings((list) => list.filter((x) => x.id !== w.id))}
            >
              Dismiss
            </button>
          </div>
          <div style={{ marginTop: '0.25rem' }}>{w.detail}</div>
        </div>
      ))}

      {items.length > 0 && (
        <div className="card">
          <div className="spread" style={{ marginBottom: '0.5rem' }}>
            <h2 style={{ margin: 0 }}>
              Uploads — {items.filter((i) => i.status === 'done').length}/{items.length}
            </h2>
            <div className="row">
              {active.length > 0 && (
                <button className="sm danger" onClick={() => abortRef.current?.abort()}>
                  Cancel remaining
                </button>
              )}
              {active.length === 0 && (
                <button className="sm ghost" onClick={() => setItems([])}>
                  Clear list
                </button>
              )}
            </div>
          </div>

          {failed.length > 0 && (
            <div className="notice" style={{ marginBottom: '0.5rem' }}>
              {failed.length} file{failed.length === 1 ? '' : 's'} failed. Drop them in again — completed chunks are
              kept, so they resume rather than restart.
            </div>
          )}

          {items.map((it) => (
            <div key={it.id} className="upload-item">
              <div className="spread">
                <span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.file.name}
                </span>
                <span className="tiny">
                  {it.status === 'failed' ? it.error : `${Math.round(it.progress * 100)}% · ${formatBytes(it.file.size)}`}
                </span>
              </div>
              <div className="bar">
                <div
                  style={{
                    width: `${Math.round(it.progress * 100)}%`,
                    background: it.status === 'failed' ? '#a3231f' : undefined,
                  }}
                />
              </div>
              <span className="tiny">{it.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
